/**
 * Preview store — read-only gallery item preview shown in ComicViewer.
 *
 * Two modes:
 *   - Project preview (from previewESSProject / essProjectPreview):
 *       shows character/scene/beat thumbnails. No pages.
 *   - Comic preview (from previewESSComic / essComicPreview):
 *       shows comic pages. No entity thumbnails.
 *
 * Cleared on unmount so preview state doesn't leak between visits.
 */

import { create } from "zustand";
import type { ComicPage } from "@amzn/a4k-creator-studio-shared";

export type PreviewKind = "project" | "comic";

/** Entity item with optional base64 image — same shape for chars/scenes/beats. */
export interface PreviewEntity {
  characterId?: string;
  sceneId?: string;
  beatId?: string;
  name?: string;
  action?: string;
  order?: number;
  visualDescription?: string;
  imageBase64?: string;
  imageId?: string;
}

interface PreviewState {
  kind: PreviewKind | null;
  /** storyId — always present. For comic previews this is the parent project's storyId. */
  storyId?: string;
  /** comicId — only set in comic previews. */
  comicId?: string;
  title: string;
  // Project-preview payload:
  characters: PreviewEntity[];
  scenes: PreviewEntity[];
  beats: PreviewEntity[];
  // Comic-preview payload:
  pages: ComicPage[];
  currentPage: number;

  isLoading: boolean;
  error: string | null;

  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  setProjectPreview: (data: {
    storyId: string;
    title?: string;
    characters: PreviewEntity[];
    scenes: PreviewEntity[];
    beats: PreviewEntity[];
  }) => void;

  setComicPreview: (data: {
    comicId: string;
    storyId: string;
    title: string;
    pages: ComicPage[];
  }) => void;

  setCurrentPage: (page: number) => void;
  clear: () => void;
}

const initialState = {
  kind: null as PreviewKind | null,
  storyId: undefined,
  comicId: undefined,
  title: "",
  characters: [] as PreviewEntity[],
  scenes: [] as PreviewEntity[],
  beats: [] as PreviewEntity[],
  pages: [] as ComicPage[],
  currentPage: 0,
  isLoading: false,
  error: null as string | null,
};

export const usePreviewStore = create<PreviewState>((set) => ({
  ...initialState,

  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error, isLoading: false }),

  setProjectPreview: (data) =>
    set({
      kind: "project",
      storyId: data.storyId,
      comicId: undefined,
      title: data.title || "Untitled Story",
      characters: data.characters,
      scenes: data.scenes,
      beats: [...data.beats].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      pages: [],
      currentPage: 0,
      isLoading: false,
      error: null,
    }),

  setComicPreview: (data) =>
    set({
      kind: "comic",
      storyId: data.storyId,
      comicId: data.comicId,
      title: data.title,
      characters: [],
      scenes: [],
      beats: [],
      pages: [...data.pages].sort((a, b) => a.pageNumber - b.pageNumber),
      currentPage: 0,
      isLoading: false,
      error: null,
    }),

  setCurrentPage: (currentPage) => set({ currentPage }),
  clear: () => set(initialState),
}));

if (__DEV__ && typeof window !== "undefined") {
  (window as any).usePreviewStore = usePreviewStore;
}
